import React, {ReactElement, useState} from 'react';
// TODO: INSTEAD OF USING MATERIAL UI COMPONENTS DIRECTLY, CREATE GENERIC COMPONENTS IN SYSTEM AND IMPORT IT
import {
  Card,
  CardContent,
  // Divider,
  Typography,
  makeStyles
} from '@material-ui/core';
import {useSnackbar} from 'notistack';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';

import {APP, DEFAULT_WHITE_COLOR} from 'src/style/variable';
import HelperUtils from '@shared/utils/HelperUtils';
import {getPaymentAuditByClinic} from '@shared/services/PaymentHistoryService';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import MinGridTable, {
  ColumnDef
} from '@components/system/MinGridTable/MinGridTable';
import WithPermission from '@components/system/WithPermission/WithPermission';
import {AppPermissions} from '@shared/constants/Mapping';

const PatientActivityCardStyles = makeStyles(() => ({
  DetailsView: {
    display: 'flex',
    flexDirection: 'column',
    fontSize: '1rem',
    rowGap: '1rem',
    overflowY: 'auto',
    maxHeight: '15.5rem'
  },
  patientActivityMainView: {
    flexDirection: 'row',
    display: 'flex',
    paddingBottom: '0.6rem'
  },
  patientActivityMainViews: {
    flexDirection: 'row',
    display: 'flex',
    alignItems: 'center',
    padding: '0.7rem'
    // paddingLeft: '0.5rem',
    // paddingTop: '0.5rem',
    // paddingBottom: '0.5rem'
  },
  patientActivityHeaderValue: {
    color: APP.STATUS.DEFAULT,
    fontSize: '1rem',
    padding: '1rem'
  },
  patientActivityType: {
    // width: '33%',
    color: '#343434'
  },
  textOverFlow: {
    fontSize: '1rem',
    textOverflow: 'ellipsis',
    overflowWrap: 'break-word',
    paddingRight: '0.2rem',
    flex: 1
    // width: '37%'
  },
  patientActivityLoginDetailsView: {
    // width: '37%',
    color: '#8C8C8C'
  },
  patientActivityDateView: {
    // width: '26%',
    color: '#343434'
  },
  applyOddColor: {
    backgroundColor: 'rgb(0,0,0,0.05)'
  },
  applyEvenColor: {
    backgroundColor: DEFAULT_WHITE_COLOR
  },
  noPatientActivityInfoView: {
    flexDirection: 'row',
    display: 'flex'
  },
  divider: {
    marginTop: '0.5rem',
    marginBottom: '0.5rem'
  },
  cardRoot: {
    '& .MuiCardContent-root:last-child': {
      paddingBottom: '13px'
    }
  },
  setPadding: {
    // padding: '0.8rem'
  },
  setPaddingLeft: {
    paddingLeft: '0.5rem'
  },
  sectionRow: {
    display: 'grid',
    gridTemplateColumns: '100%',
    fontSize: '1.3rem',
    paddingBlock: '1rem'
  },
  DrawerInfo: {
    fontSize: '1.3rem',
    fontWeight: 600
  },
  amountWrapper: {
    display: 'flex',
    justifyContent: 'flex-start',
    paddingLeft: '0.5rem'
  },
  cardHeader: {
    '& .MuiCardContent-root': {
      padding: '0'
    }
  },
  loaderStyle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
}));

interface PatientActivityCardProps {
  guarantorId: number | string;
  clinicId?: number | string;
}

const PatientActivityCard = (props: PatientActivityCardProps): ReactElement => {
  const classes = PatientActivityCardStyles();
  const {guarantorId, clinicId} = props;

  const {t} = useTranslation();
  // FIXME: ADD PROPER TYPES
  const groupId: any = useSelector((state: any) => state.auth.groupId);

  const selectedClinicId = useSelector(
    (state: any) => state.clinics.selectedClinicId
  );
  const [
    isLoadingPatientActivity,
    setIsLoadingPatientActivity
  ] = useState<boolean>(true);
  const [patientActivities, setPatientActivities]: any = useState([]);

  const {enqueueSnackbar} = useSnackbar();

  const getPatientsActivities = async (): Promise<void> => {
    try {
      const options = {
        guarantor_id: guarantorId
      };

      const patientActivitiesResponse = await getPaymentAuditByClinic(
        groupId,
        (selectedClinicId && selectedClinicId[0]) ?? clinicId,
        options
      );
      if (patientActivitiesResponse?.data?.rows?.length > 0) {
        setPatientActivities(patientActivitiesResponse?.data?.rows);
      }
    } catch (error: any) {
      enqueueSnackbar(error && error.message, SnackBarConfig('e'));
    } finally {
      setIsLoadingPatientActivity(false);
    }
  };

  React.useEffect(() => {
    if (guarantorId) {
      getPatientsActivities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patientActivityColDef: ColumnDef<any>[] = [
    {
      id: 'type',
      label: t('PATIENT_DETAILS_CARD.ACTIVITY_TYPE'),
      render(item) {
        return item.action ? t(`ACTION_HISTORY.${item.action}`) : '—';
      }
    },
    {
      id: 'login-details',
      label: t('PATIENT_DETAILS_CARD.LOGIN_DETAILS'),
      render(item) {
        return item.metadata?.login_details ? (
          <div>
            {t('ACTION_HISTORY.LAST_NAME')}:{' '}
            {item.metadata.login_details?.last_name ?? '—'} <br />
            {t('ACTION_HISTORY.FIRST_NAME')}:{' '}
            {item.metadata.login_details?.first_name ?? '—'}
            {item.metadata.login_details?.dob ? (
              <>
                <br />
                {t('ACTION_HISTORY.DOB')}:{' '}
                {HelperUtils.formatDisplayDate(
                  item.metadata.login_details?.dob
                )}
              </>
            ) : null}
          </div>
        ) : (
          '—'
        );
      }
    },
    {
      id: 'date-time',
      label: t('PATIENT_DETAILS_CARD.DATE_AND_TIME'),
      render(item) {
        return HelperUtils.formatDisplayDateTime(item?.created_at);
      }
    }
  ];

  return (
    <>
      <Typography
        data-test-id="pcl-payments-guarantorLoginActivityHeading"
        className={[classes.sectionRow, classes.DrawerInfo].join(' ')}>
        {t('PATIENT_DETAILS_CARD.PATIENT_ACTIVITY')}
      </Typography>
      <Card className={classes.cardHeader}>
        <CardContent>
          <MinGridTable
            noDataText={t('PATIENT_DETAILS_CARD.NO_PATIENT_ACTIVITY')}
            colDefs={patientActivityColDef}
            rows={patientActivities}
            isLoading={isLoadingPatientActivity}
          />
        </CardContent>
      </Card>
    </>
  );
};

export default WithPermission(
  PatientActivityCard,
  AppPermissions.ACTION_HISTORY.NAME,
  AppPermissions.ACTION_HISTORY.LIST
);
